/**
 * The ONE translation of an accepted media promise into the art-plan design
 * every composed image producer generates from (the in-build media child,
 * the follow-up art pass, studio strips), and the per-slot GENERATION BASIS
 * derived from it: exactly the inputs a slot's art is generated and judged
 * from, so a revision can tell which slots' art is still the art the revised
 * game would draw ({@link compositionSlotBasisV1}).
 *
 * Pure and data-only: the builder composes the provider prompts from this
 * design (its plan composer), and admission reads the basis.
 */
import { canonicalJson } from '@djgg/gameplay-kit';
import { hexHueName } from '@djgg/gameplay-kit/compose';
import { stripAssetName } from '../animation.js';
import type { GameDesign, SpriteOrientationContract } from '../design/types.js';
import type { CompositionMediaRefV1 } from '../compositionLineage.js';
import type { AssetGenJob } from '../types.js';
import type { AcceptedCompositionV1 } from './gameSpec.js';
import { COMPOSED_PROFILE, composedTemplateKindV1 } from './composedSpec.js';
import {
  COMPOSITION_BACKGROUND_SLOT, COMPOSITION_MUSIC_SLOT, compositionFacingV1, compositionGeneratedEntitiesV1, compositionPresentationV1,
  compositionRecolorSourceHexV1, compositionSettingsContractV1, compositionTerrainTileV1, type CompositionPresentationV1,
} from './compositionPresentation.js';

const SPRITE_PROMPT_MAX = 300;
/** The dominant-colour clause of a recolour SOURCE kind's subject line: its
 * palette colour ({@link compositionRecolorSourceHexV1}), by name and exact
 * hex. Deterministic; the one renderer of it. */
export function recolorSourceCue(hex: string): string {
  return `its dominant colour is ${hexHueName(hex)} (${hex}) across most of its body`;
}

/** Below this width:height (or its inverse) a box reads as square-ish. */
const ASPECT_CUE_MIN = 1.15;
/** Beyond this a proportion is stated as a long strip, not a number: the
 * generator draws each subject in a square sheet cell, and asked for "10:1"
 * it drew a pipe-like bar or broken segments (real probe, platform 10:1). */
const ASPECT_CUE_STRIP = 3;

type PresentationEntity = CompositionPresentationV1['entities'][string];

/** The geometry cue a NON-terrain slot's render box implies: a surface drawn
 * once (a sign) skins its whole box, and any clearly non-square box states its
 * proportion. A terrain tile's shape is its structured `surfaceTile` (the plan
 * composer's one terrain clause), and facing is the structured orientation
 * contract below. Kept short: the art line plus this cue must fit
 * {@link SPRITE_PROMPT_MAX}. */
function shapeCue(render: PresentationEntity['render']): string {
  const ratio = render.size.w / render.size.h;
  const aspect = ratio >= ASPECT_CUE_STRIP ? 'one long continuous horizontal strip, much wider than tall'
    : ratio >= ASPECT_CUE_MIN ? `wider than tall, about ${ratio.toFixed(1)}:1`
      : ratio <= 1 / ASPECT_CUE_STRIP ? 'one long continuous vertical strip, much taller than wide'
        : ratio <= 1 / ASPECT_CUE_MIN ? `taller than wide, about 1:${(1 / ratio).toFixed(1)}` : '';
  const fill = render.surface ? 'a solid shape that fills its whole frame edge to edge' : '';
  return [fill, aspect].filter(Boolean).join(', ');
}

/** The ONE image-space orientation contract for a kind, derived from the
 * presentation's projection and the kind's declared (or role-default)
 * facing. Generation prints it, the judge grades it (`orientationMatch`, a
 * core criterion) on the base sprite AND every strip, so a strip can never
 * turn its subject away from the base. A terrain tile has no facing: it is
 * the level's surface in the game's own view — from above in a top-down
 * game, else side-on — never a front-projected object. */
export function compositionOrientationV1(
  projection: CompositionPresentationV1['projection'],
  entity: PresentationEntity,
): SpriteOrientationContract {
  const facing = compositionFacingV1(entity);
  const moves = entity.states.includes('move');
  const pivot = entity.render.anchor === 'bottom' ? { x: 0.5, y: 1 } : { x: 0.5, y: 0.5 };
  if (compositionTerrainTileV1(entity)) {
    return {
      projection: projection === 'top-down' ? 'top-down' : 'side', canonicalFacing: 'none', forwardVector: { x: 0, y: 0 }, pivot,
      movementAxis: 'stationary', mirrorSafe: true, rotationPolicy: 'none',
    };
  }
  if (facing === 'diagonal') {
    return {
      projection: 'three-quarter', canonicalFacing: 'up-right', forwardVector: { x: 1, y: -1 }, pivot,
      movementAxis: moves ? 'omnidirectional' : 'stationary', mirrorSafe: false, rotationPolicy: 'none',
    };
  }
  if (facing === 'side' && projection !== 'front') {
    return {
      projection: projection === 'top-down' ? 'top-down' : 'side', canonicalFacing: 'right',
      forwardVector: { x: 1, y: 0 }, pivot,
      movementAxis: !moves ? 'stationary' : projection === 'top-down' ? 'omnidirectional' : 'horizontal',
      mirrorSafe: true, rotationPolicy: 'none',
    };
  }
  return {
    projection: facing === 'symmetric' ? 'symmetric' : projection === 'top-down' ? 'top-down' : 'front',
    canonicalFacing: 'none', forwardVector: { x: 0, y: 0 }, pivot,
    movementAxis: moves ? (projection === 'side' ? 'horizontal' : 'omnidirectional') : 'stationary',
    mirrorSafe: true, rotationPolicy: 'none',
  };
}

/** The ONE translation of an accepted media promise into the old pipeline's
 * art-plan design: every render entity kind becomes a hero sprite whose prompt
 * is the creator-approved appearance plus its geometry cue, whose orientation
 * contract is its declared facing and whose label is its declared sign; its
 * declared animation states become the strips the strip planner draws, and the
 * background slot becomes the single scenery background, carrying the game's
 * surface contact so the background's scenery rule is this game's
 * (`designSceneryRuleV1`: these entities, these surfaces). The image prompt —
 * which is also the judge's intended subject — is the reviewed art line alone,
 * a subject-only line by contract (`subjectOnly.ts` in @djgg/creations): a
 * line naming its subject's situation ("held captive beneath a guardian")
 * makes the image model bake the other object in and the judge demand it.
 * The kind's role rides only as the entity role. A kind another kind's art is
 * recoloured from also names its palette colour as its dominant colour
 * ({@link recolorSourceCue}) — in its sprite AND, through the same subject
 * line, every one of its strips and both judges' intended subject: the recolour
 * moves only that colour, so a source drawn without it derives a twin.
 * Gameplay text is not part of an art plan, so the remaining fields only name
 * the maintained game. */
export function compositionMediaDesign(authority: AcceptedCompositionV1): GameDesign {
  // A derived kind is recoloured from its source's accepted art: it is never
  // planned, generated or judged.
  const media = authority.media;
  if (!media) throw new Error('Composition has no media promise');
  const presentation = compositionPresentationV1(authority.spec);
  const art = media.artDirection;
  return {
    summary: `"${authority.spec.title}", ${presentation.summary}.`,
    coreVerbs: ['play'],
    mechanics: ['Platform-maintained gameplay; this plan covers art only.'],
    entities: compositionGeneratedEntitiesV1(presentation).map(([kind, entity]) => {
      const terrain = compositionTerrainTileV1(entity);
      const cue = terrain ? '' : shapeCue(entity.render);
      const colour = compositionRecolorSourceHexV1(presentation, kind);
      return {
        name: kind,
        role: entity.role,
        sprite: {
          hero: true,
          tint: entity.art === 'neutral' ? 'neutral' as const : 'fixed' as const,
          // The colour clause sits before the cue: an art line (≤ 200 chars)
          // plus it always fits SPRITE_PROMPT_MAX, so only a cue is ever cut.
          prompt: [art.slots[kind], colour ? recolorSourceCue(colour) : '', cue].filter(Boolean).join('; ').slice(0, SPRITE_PROMPT_MAX),
          orientation: compositionOrientationV1(presentation.projection, entity),
          ...(terrain ? { surfaceTile: { aspect: entity.render.size.w / entity.render.size.h } } : {}),
          ...(entity.label ? { label: entity.label } : {}),
          ...(entity.states.length ? { animations: entity.states.map(state => ({ state })) } : {}),
        },
      };
    }),
    controls: 'Platform-maintained controls.',
    winCondition: 'Platform-maintained objective.',
    theme: art.style,
    scenery: {
      backgrounds: [{ name: COMPOSITION_BACKGROUND_SLOT, prompt: art.slots[COMPOSITION_BACKGROUND_SLOT]! }],
      surfaceContact: presentation.surfaceContact,
    },
  };
}


/** One carryable slot's GENERATION BASIS: everything its art is generated
 * and judged from — for a sprite its design entity (role, the reviewed art
 * line with its colour and geometry cues, tint, orientation contract,
 * terrain tile, sign) under the shared style block (style, whether any
 * subject is labelled, the game's orientation); for a strip that sprite
 * basis and its state; for the background its scenery line and surface
 * contact under the style block; for the soundtrack its closed brief.
 * `key` is the slot as its TEMPLATE names it (a kind through the composed
 * patch's renames; {@link composedTemplateKindV1}), the identity a slot
 * keeps across a renaming revision of the same template. */
export interface CompositionSlotBasisV1 {
  slot: string;
  key: string;
  /** Canonical JSON of the basis: two slots' art is interchangeable exactly
   * when their bases are byte-identical. */
  basis: string;
}

/** The generation basis of every slot an accepted media promise could carry
 * forward, sorted by slot: every GENERATED kind's sprite and strips, the
 * background and a promised soundtrack. A derived kind's slots (recoloured
 * from their source, never generated) and the gallery cover (always
 * regenerated) have none. */
export function compositionSlotBasisV1(authority: AcceptedCompositionV1): CompositionSlotBasisV1[] {
  if (!authority.media) return [];
  const spec = authority.spec;
  const design = compositionMediaDesign(authority);
  const presentation = compositionPresentationV1(spec);
  const keyOf = (kind: string) => spec.profile === COMPOSED_PROFILE ? composedTemplateKindV1(spec.patch, kind) : kind;
  const look = { style: design.theme ?? '', labelled: design.entities.some(entity => !!entity.sprite?.label),
    orientation: compositionSettingsContractV1(spec).orientation };
  const rows: CompositionSlotBasisV1[] = [];
  for (const entity of design.entities) {
    // The sprite image never depends on which strips its kind animates.
    const { animations: _animations, ...sprite } = entity.sprite!;
    const spriteBasis = { slotKind: 'sprite', ...look, role: entity.role, sprite };
    rows.push({ slot: entity.name, key: keyOf(entity.name), basis: canonicalJson(spriteBasis) });
    for (const state of presentation.entities[entity.name]!.states) {
      rows.push({ slot: stripAssetName(entity.name, state), key: stripAssetName(keyOf(entity.name), state),
        basis: canonicalJson({ slotKind: 'strip', state, sprite: spriteBasis }) });
    }
  }
  rows.push({ slot: COMPOSITION_BACKGROUND_SLOT, key: COMPOSITION_BACKGROUND_SLOT, basis: canonicalJson({ slotKind: 'background', ...look,
    prompt: design.scenery!.backgrounds![0]!.prompt, surfaceContact: design.scenery!.surfaceContact ?? false }) });
  if (authority.media.slots.includes(COMPOSITION_MUSIC_SLOT)) {
    rows.push({ slot: COMPOSITION_MUSIC_SLOT, key: COMPOSITION_MUSIC_SLOT, basis: canonicalJson({ slotKind: 'music', brief: presentation.music }) });
  }
  return rows.sort((a, b) => a.slot.localeCompare(b.slot));
}

/** Who made a predecessor's bound art: the platform (an in-build
 * composition media child, or the follow-up art pass, each drawing from the
 * live game's basis) or the creator (anything attached through the studio:
 * a creator generation, an upload, a rebind). Decided from the variant's
 * generation job alone (null: no job, an upload). */
export function compositionMediaOriginV1(genJob: Pick<AssetGenJob, 'measuredBudget' | 'completion'> | null): 'generated' | 'attached' {
  return genJob && (genJob.measuredBudget?.role === 'composition-media@2' || !!genJob.completion) ? 'generated' : 'attached';
}

/** One slot of a revised game whose art is carried, unchanged, from its
 * predecessor's published media instead of being generated again. */
export interface CompositionMediaCarryV1 {
  /** The revised game's slot. */
  slot: string;
  /** The predecessor's bound variant (its own slot name, namespace and bytes). */
  from: CompositionMediaRefV1;
  /** `same-basis`: platform art whose generation basis is byte-identical;
   * `creator-attached`: art the creator attached, kept while its slot exists. */
  reason: 'same-basis' | 'creator-attached';
}

/** THE carry policy of a composed revision, sorted by slot. Of the SAME
 * template: platform art is kept for a slot iff its generation basis is
 * byte-identical (matched by template key through renames), creator-attached
 * art for every slot that still exists. Onto ANOTHER template (the fresh
 * route): only creator-attached art whose slot name and media kind the new
 * game still has; platform art is redrawn. From a GENERIC predecessor
 * (`predecessor` null): every asset it had attached — whoever made it, the
 * old pipeline included — whose name and kind is a slot of the new game (its
 * `bound` never holds a tint-neutral kind's art: {@link deriveCompositionRevisionV1}). Derived slots are re-derived and the cover regenerated
 * (neither has a basis); a strip is kept only with its kind's sprite, which
 * anchors it. `bound` is the predecessor's published media (its final
 * receipt's variants, in their namespaces) with each variant's origin
 * ({@link compositionMediaOriginV1}). */
export function compositionRevisionCarryV1(input: {
  predecessor: AcceptedCompositionV1 | null;
  revised: AcceptedCompositionV1;
  /** Each bound variant with who made it and its asset kind. */
  bound: ReadonlyArray<{ ref: CompositionMediaRefV1; origin: 'generated' | 'attached'; kind: string }>;
}): CompositionMediaCarryV1[] {
  const sameTemplate = !!input.predecessor && input.predecessor.spec.profile === COMPOSED_PROFILE && input.revised.spec.profile === COMPOSED_PROFILE
    && input.predecessor.spec.template === input.revised.spec.template
    && canonicalJson(input.predecessor.spec.generatedTemplate ?? null) === canonicalJson(input.revised.spec.generatedTemplate ?? null);
  const slotKind = (row: { basis: string }): unknown => (JSON.parse(row.basis) as { slotKind?: unknown }).slotKind;
  const priorBasis = new Map(input.predecessor ? compositionSlotBasisV1(input.predecessor).map(row => [row.slot, row.basis]) : []);
  const prior = new Map(input.predecessor
    ? compositionSlotBasisV1(input.predecessor).map(row => [sameTemplate ? row.key : row.slot, row.slot])
    : input.bound.map(entry => [entry.ref.slot, entry.ref.slot]));
  const bound = new Map(input.bound.map(entry => [entry.ref.slot, entry]));
  const revised = compositionSlotBasisV1(input.revised);
  const carried = new Map<string, CompositionMediaCarryV1>();
  for (const row of revised) {
    const before = prior.get(sameTemplate ? row.key : row.slot);
    const entry = before ? bound.get(before) : undefined;
    if (!before || !entry || (!sameTemplate && entry.kind !== slotKind(row))) continue;
    if (entry.origin === 'attached' || !input.predecessor) carried.set(row.slot, { slot: row.slot, from: structuredClone(entry.ref), reason: 'creator-attached' });
    else if (sameTemplate && priorBasis.get(before) === row.basis) carried.set(row.slot, { slot: row.slot, from: structuredClone(entry.ref), reason: 'same-basis' });
  }
  const presentation = compositionPresentationV1(input.revised.spec);
  for (const [kind, entity] of Object.entries(presentation.entities)) {
    if (carried.has(kind)) continue;
    for (const state of entity.states) carried.delete(stripAssetName(kind, state));
  }
  return [...carried.values()].sort((a, b) => a.slot.localeCompare(b.slot));
}
